import {FC, useState} from 'react';
import styled, {useTheme} from 'styled-components';
import {ManageGoalTags} from '@pages/1:1s/create-meetings/goal-tags';
import {NoBorderButton} from '@pages/dashboard/reporting/reporting-pages/reporting-overview/styles';
import {Button} from '@ui/atoms/button';
import {MinusIcon} from '@ui/atoms/icons';
import {ThickPlusIcon} from '@ui/atoms/icons/thick-plus';
import {Label} from '@ui/atoms/label';
import {VerticalSpacer} from '@ui/atoms/spacer/spacer';
import {TextAreaNoBorder} from '@ui/atoms/textArea/textarea';
import {Body2} from '@ui/atoms/typography';
import {Modal} from '@ui/molecules/modal';
import {SelectField} from '@ui/molecules/select/selectfield';
import {FlexRow} from '@ui/style/styles';
import {getColorFromTheme} from '@ui/ui-utils';
import {useTags} from '@hooks/tags';
import {writeFirebaseData} from '@utils/firebase-handler';

const InputWrapper = styled.div`
  margin-top: 8px;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 10px;
  min-height: 140px;
  background-color: ${getColorFromTheme('white')};
  padding: 16px;

  .description {
    padding-top: 6px;
  }
`;

const DescriptionHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const DescriptionLabel = styled.p`
  color: ${getColorFromTheme('blackTwo')};
  font: 600 14px/22px Graphik, sans-serif;
  margin: 0;
`;

const OptionalLabel = styled.span`
  color: ${getColorFromTheme('textBody')};
  font-weight: 400;
`;

const InlineOptionalPill = styled.span`
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 8px;
  background: ${getColorFromTheme('backgroundDark')};
  color: ${getColorFromTheme('textBody')};
  font-size: 12px;
  line-height: 20px;
`;

const RemoveButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  color: ${getColorFromTheme('red400')};
  cursor: pointer;
  display: inline-flex;
  font: 600 14px/22px Graphik, sans-serif;
  gap: 8px;
  padding: 0;

  svg {
    height: 16px;
    width: 16px;
  }

  svg path {
    stroke: currentColor;
  }

  &:focus-visible {
    border-radius: 4px;
    outline: 2px solid ${getColorFromTheme('purple200')};
    outline-offset: 3px;
  }
`;

const DescriptionDisplay = styled.button`
  background: ${getColorFromTheme('white')};
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  color: ${getColorFromTheme('textBody')};
  cursor: text;
  display: block;
  font: 400 14px/22px Graphik, sans-serif;
  margin-top: 8px;
  padding: 16px;
  text-align: left;
  width: 100%;

  &:hover,
  &:focus-visible {
    border-color: ${getColorFromTheme('purple200')};
    outline: none;
  }
`;

const ConfirmationDialog = styled.div`
  background: ${getColorFromTheme('white')};
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 12px;
  box-shadow: 0 16px 48px rgba(30, 30, 47, 0.16);
  margin: 16vh auto 0;
  max-width: 480px;
  overflow: hidden;
  width: calc(100% - 32px);
`;

const ConfirmationHeader = styled.div`
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};
  padding: 18px 20px;

  h2 {
    color: ${getColorFromTheme('blackTwo')};
    font: 600 18px/26px Graphik, sans-serif;
    margin: 0;
  }
`;

const ConfirmationBody = styled.div`
  color: ${getColorFromTheme('textBody')};
  font: 400 14px/22px Graphik, sans-serif;
  padding: 20px;

  p {
    margin: 0;
  }
`;

const ConfirmationFooter = styled.div`
  align-items: center;
  background: ${getColorFromTheme('backgroundLight')};
  border-top: 1px solid ${getColorFromTheme('borderLight')};
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding: 16px 20px;

  button {
    height: 42px;
    padding: 9px 18px;
  }
`;

const DangerButton = styled(Button)`
  && {
    background-color: ${getColorFromTheme('red400')};
    border-color: ${getColorFromTheme('red400')};
    color: ${getColorFromTheme('alwaysWhite')};
  }

  &&:not(:disabled):hover {
    background-color: ${getColorFromTheme('red400')};
    border-color: ${getColorFromTheme('red400')};
    color: ${getColorFromTheme('alwaysWhite')};
    filter: brightness(0.94);
  }

  &&:not(:disabled):focus,
  &&:not(:disabled):focus-visible {
    background-color: ${getColorFromTheme('red400')};
    border-color: ${getColorFromTheme('red400')};
    box-shadow: rgba(213, 42, 42, 0.18) 0 0 0 4px;
    color: ${getColorFromTheme('alwaysWhite')};
  }
`;

interface IAddDescription {
  onChange: (data: {tags: string[]; description: string}) => void;
  isGoalKpi?: boolean;
  value?: {tags: string[]; description: string};
}

const AddDescriptionAndTags: FC<IAddDescription> = ({
  onChange,
  value,
  isGoalKpi,
}) => {
  const theme = useTheme();
  const {tags} = useTags('goal_tags');
  const initialDescription = value?.description || '';

  const [manageTags, setManageTags] = useState(false);
  const [addDescription, setAddDescription] = useState(
    Boolean(initialDescription),
  );
  const [tagValue, setTagValue] = useState(value?.tags?.[0] || '');
  const [description, setDescription] = useState(initialDescription);
  const [editingDescription, setEditingDescription] = useState(false);
  const [removeConfirmationOpen, setRemoveConfirmationOpen] = useState(false);

  const selectedTags = tagValue ? [tagValue] : value?.tags || [];

  const saveDescriptionOnBlur = () => {
    if (!description.trim()) {
      setDescription('');
      setAddDescription(false);
      setEditingDescription(false);
      onChange({tags: selectedTags, description: ''});
      return;
    }

    onChange({tags: selectedTags, description});
    setEditingDescription(false);
  };

  const deleteDescription = () => {
    setDescription('');
    setEditingDescription(false);
    setRemoveConfirmationOpen(false);
    setAddDescription(false);
    onChange({tags: selectedTags, description: ''});
  };

  const formattedTags = tags
    .filter((tag) => !!tag.tag?.name)
    .map((tag) => ({
      ...tag,
      tag: tag.tag?.name,
      goals: tag.tag?.goals,
    }));

  return (
    <div>
      {!addDescription && (
        <NoBorderButton
          type="button"
          onClick={() => {
            setAddDescription(true);
            setEditingDescription(true);
          }}>
          <FlexRow>
            <ThickPlusIcon color={theme.colors.green300} />
            <Body2 weight="bold" style={{fontSize: '12px', marginLeft: '6px'}}>
              Add description
            </Body2>
            <InlineOptionalPill>Optional</InlineOptionalPill>
          </FlexRow>
        </NoBorderButton>
      )}

      {addDescription && (
        <>
          <DescriptionHeader>
            <DescriptionLabel>
              Description{' '}
              {editingDescription && <OptionalLabel>(optional)</OptionalLabel>}
            </DescriptionLabel>
            {!editingDescription && (
              <RemoveButton
                type="button"
                onClick={() => setRemoveConfirmationOpen(true)}>
                <MinusIcon />
                Remove
              </RemoveButton>
            )}
          </DescriptionHeader>

          {editingDescription ? (
            <InputWrapper>
              <div className="description">
                <TextAreaNoBorder
                  autoFocus
                  value={description}
                  maxLength={300}
                  style={{
                    padding: '5px 0px',
                    fontSize: '14px',
                    backgroundColor: 'transparent',
                  }}
                  onBlur={saveDescriptionOnBlur}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="Add a description (max. 300 characters)"
                />
              </div>
            </InputWrapper>
          ) : (
            <DescriptionDisplay
              type="button"
              aria-label="Edit description"
              onClick={() => setEditingDescription(true)}>
              {description}
            </DescriptionDisplay>
          )}
        </>
      )}

      <Modal
        open={removeConfirmationOpen}
        onClose={() => setRemoveConfirmationOpen(false)}
        zIndex={20}>
        <ConfirmationDialog
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="remove-description-title">
          <ConfirmationHeader>
            <h2 id="remove-description-title">Remove description?</h2>
          </ConfirmationHeader>
          <ConfirmationBody>
            <p>
              This will remove the description from this goal. This action
              cannot be undone.
            </p>
          </ConfirmationBody>
          <ConfirmationFooter>
            <Button
              type="button"
              kind="secondary"
              onClick={() => setRemoveConfirmationOpen(false)}>
              Cancel
            </Button>
            <DangerButton type="button" onClick={deleteDescription}>
              Remove description
            </DangerButton>
          </ConfirmationFooter>
        </ConfirmationDialog>
      </Modal>

      {isGoalKpi && (
        <div className="relative mt-4">
          <Label>
            Tags <OptionalLabel>(optional)</OptionalLabel>
          </Label>
          <VerticalSpacer size="8px" />

          <SelectField
            placeholder="# - add tag"
            performance
            type="dropdownOnly"
            excludeSearchBox
            showCancelIcon={false}
            margin
            borderRadius="10px"
            defaultValue={value?.tags?.[0]}
            noOptionMessage="No option Found"
            maxSelection={1}
            groupDisplayLength={3}
            onClickCustomOption={() => setManageTags(true)}
            customOption
            customOptionName="Manage tags"
            options={[
              ...(formattedTags
                ? formattedTags.map((tag) => ({
                    value: tag.tag,
                    label: tag.tag,
                  }))
                : []),
              {
                label: 'Custom',
                value: 'custom-option',
              },
            ]}
            onChange={(data: {value: string; label: any | null}) => {
              if (data?.value.toLowerCase() === 'custom-option') return;

              setTagValue(data?.value || '');
              onChange({
                tags: data?.value ? [data?.value] : [],
                description,
              });
            }}
          />
        </div>
      )}

      {manageTags && (
        <ManageGoalTags
          open={manageTags}
          onClose={() => setManageTags(false)}
          tags={formattedTags}
          onSubmit={(tag) => {
            writeFirebaseData('goal_tags', {
              name: tag,
              goals: [],
            });
          }}
        />
      )}
    </div>
  );
};

export default AddDescriptionAndTags;
